import React, { ReactElement, useMemo } from 'react';
import { CoachLicenceItemApiInterface } from 'submodules/api_middleware';
import { COACH_LICENCE_ALERT_DAYS } from 'constants/general';
import { toDate } from 'utils/helpers/datetime';
import { useTranslation, getTranslation } from 'utils/localization';
import { TNamespaceEnum } from 'types/types';
import styles from './LicencesCell.module.scss';

export type LicenceTone = 'expired' | 'expiringSoon' | 'valid' | 'unknown';

export const getLicenceValidToTone = (
    validTo: string | null | undefined
): LicenceTone => {
    if (!validTo) {
        return 'unknown';
    }
    const end = new Date(validTo);
    end.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const alertEnd = new Date(today);
    alertEnd.setDate(alertEnd.getDate() + COACH_LICENCE_ALERT_DAYS);

    if (end < today) {
        return 'expired';
    }
    if (end <= alertEnd) {
        return 'expiringSoon';
    }
    return 'valid';
};

const toneClass = (tone: LicenceTone): string => {
    switch (tone) {
        case 'expired':
            return styles.expired;
        case 'expiringSoon':
            return styles.expiringSoon;
        case 'valid':
            return styles.valid;
        default:
            return '';
    }
};

interface Props {
    licences: CoachLicenceItemApiInterface[];
}

const LicencesCell = ({ licences }: Props): ReactElement => {
    const { t } = useTranslation();

    const statusTitle = useMemo(
        () => ({
            expired: t('CoachPage.licences.expired'),
            expiringSoon: t('CoachPage.licences.expiringSoon'),
            valid: t('CoachPage.licences.valid'),
            unknown: '',
        }),
        [t]
    );

    const count = licences?.length ?? 0;

    if (!count) {
        return (
            <span className={styles.empty}>
                {getTranslation(t, TNamespaceEnum.person, 'noLicences')}
            </span>
        );
    }

    return (
        <div className={styles.wrap}>
            <div className={styles.list}>
                {licences.map((lic) => {
                    const tone = getLicenceValidToTone(lic.valid_to);
                    const datePart = lic.valid_to ? toDate(lic.valid_to) : '—';
                    const namePart = lic.licence_type_name ?? '—';
                    const label = `${namePart} – ${datePart}`;
                    const title =
                        tone === 'unknown' ? undefined : statusTitle[tone];
                    return (
                        <div
                            key={lic.id}
                            className={`${styles.row} ${toneClass(tone)}`}
                            title={title || undefined}
                        >
                            {label}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default LicencesCell;
